import { db } from '../../../integrations/firebase';
import { ref, push, set, update } from 'firebase/database';
import { recordTimelineEvent } from '../../../core/customer/CustomerTimelineEngine';
import { CallActivityService } from '../../../core/customer/CallActivityService';
import { NEGATIVE_FEEDBACK } from '../logic/telesales';

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));
const keyOf = (m) => String(m || '').replace(/\D/g, '').slice(-10);
const uid = (u) => u?.uid || u?.email || u?.displayName || 'agent';

// Create a linked follow-up To-Do.
const createFollowUpTask = async (mobile, name, { followUpAt, assignedTo, reason }, user) => {
    const taskRef = push(ref(db, 'tasks'));
    await set(taskRef, clean({
        id: taskRef.key, title: `Follow-up call: ${name || mobile}`, phone: mobile, customerName: name || '',
        dueAt: followUpAt, assignedTo: assignedTo || '', type: 'telesales_followup', status: 'pending',
        reason: reason || '', createdBy: uid(user), createdAt: Date.now(),
    }));
    return taskRef.key;
};

export const TelesalesService = {
    // --- Call log (section 2/3) ---
    addCallLog: async (mobile, name, log, user) => {
        const key = keyOf(mobile);
        if (!key) throw new Error('Invalid mobile');
        const followUpMs = log.followUpAt ? new Date(log.followUpAt).getTime() : 0;
        // Central writer -> callActivities + customerBase/{key}/callHistory (same id)
        const callId = await CallActivityService.logCall({
            mobile, customerKey: key, customerName: name,
            sourceModule: log.sourceModule || 'customer_profile',
            callType: log.callType, result: log.result, remark: log.remark,
            productDiscussed: log.productDiscussed, offerDiscussed: log.offerDiscussed,
            customerMood: log.customerMood,
            calledBy: uid(user), calledByUid: user?.uid || '', calledByRole: user?.role || '',
            followUpAt: followUpMs || undefined, assignedTo: log.assignedTo,
            orderId: log.orderId, ticketId: log.ticketId,
        });
        await recordTimelineEvent(mobile, 'call_made', {
            note: `${log.callType || 'Call'} — ${log.result || ''}${log.remark ? `: ${log.remark}` : ''}`,
            status: log.result, source: 'telesales',
        }, uid(user));
        if (followUpMs) {
            await createFollowUpTask(mobile, name, { followUpAt: followUpMs, assignedTo: log.assignedTo, reason: `${log.callType}: ${log.remark || ''}` }, user);
            await recordTimelineEvent(mobile, 'follow_up', { note: `Follow-up scheduled for ${new Date(followUpMs).toLocaleString('en-IN')}` }, uid(user));
        }
        return { ok: true, ordered: log.result === 'Ordered', complaint: log.result === 'Complaint Raised', callId };
    },

    // Link a call to the order it converted into. The SERVER verifies the
    // order exists and takes the revenue from it — a client-supplied number
    // is never trusted, and one order can never be credited twice.
    linkCallToOrder: async (mobile, callId, orderId, _revenueIgnored) => {
        const key = keyOf(mobile);
        if (!key || !callId || !orderId) return;
        const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
        const { authHeaders } = await import('../../../core/api/authHeaders');
        const r = await fetch(`${PROXY}/api/tc/order/attribute`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ orderKey: orderId, callId, mobile: key }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.ok) throw new Error(j.error || 'Could not link the order');
    },

    // --- Telecaller remark (section 4) ---
    addTelecallerRemark: async (mobile, remark, type, user) => {
        const key = keyOf(mobile);
        if (!key) throw new Error('Invalid mobile');
        const entry = clean({ remark, type: type || 'general', createdBy: uid(user), createdAt: Date.now() });
        await set(push(ref(db, `customerBase/${key}/telecallerRemarks`)), entry);
        await update(ref(db, `customerBase/${key}`), { lastTelecallerRemark: remark, updatedAt: Date.now() });
        await recordTimelineEvent(mobile, 'remark_added', { note: remark, source: 'telesales' }, uid(user));
    },

    // --- Feedback call (section 5) ---
    addFeedbackCall: async (mobile, fb, user) => {
        const key = keyOf(mobile);
        if (!key) throw new Error('Invalid mobile');
        const negative = NEGATIVE_FEEDBACK.has(fb.result);
        const entry = clean({
            productUsed: fb.productUsed, usageDuration: fb.usageDuration, result: fb.result,
            rating: fb.rating ? Number(fb.rating) : undefined, feedback: fb.feedback,
            photoAvailable: !!fb.photoAvailable, permissionToUse: !!fb.permissionToUse,
            followUpRequired: !!fb.followUpRequired, sentiment: negative ? 'negative' : 'positive',
            createdBy: uid(user), createdAt: Date.now(),
        });
        await set(push(ref(db, `customerBase/${key}/feedbackCalls`)), entry);
        // Surface the feedback call in the unified call stream too.
        await CallActivityService.logCall({
            mobile, customerKey: key, sourceModule: 'feedback', callType: 'Feedback Call',
            result: fb.result, remark: fb.feedback, productDiscussed: fb.productUsed,
            customerMood: negative ? 'Negative' : 'Positive',
            calledBy: uid(user), calledByUid: user?.uid || '', calledByRole: user?.role || '',
        });
        // Flags for filtering
        const flags = { lastFeedbackAt: Date.now() };
        if (negative) { flags.negativeFeedback = true; if (fb.result === 'Wants Doctor Call') flags.doctorRequired = true; }
        else if (fb.result === 'Good Result') { flags.positiveFeedback = true; if (fb.permissionToUse) flags.testimonialCandidate = true; }
        await update(ref(db, `customerBase/${key}`), clean(flags));
        await recordTimelineEvent(mobile, 'manual_note', { note: `Feedback: ${fb.result}${fb.rating ? ` (${fb.rating}★)` : ''}`, status: negative ? 'negative' : 'positive', source: 'feedback' }, uid(user));
        return { ok: true, negative, testimonial: !negative && fb.result === 'Good Result' };
    },

    // --- DND + winback flags (section 3/6) ---
    setDND: async (mobile, on, user) => {
        const key = keyOf(mobile);
        await update(ref(db, `customerBase/${key}`), { dnd: !!on, dndSetBy: uid(user), updatedAt: Date.now() });
        await recordTimelineEvent(mobile, 'manual_note', { note: on ? 'Marked Do Not Disturb' : 'DND removed', source: 'telesales' }, uid(user));
    },
    markNotInterested: async (mobile, user) => {
        const key = keyOf(mobile);
        await update(ref(db, `customerBase/${key}`), { notInterested: true, updatedAt: Date.now() });
        await recordTimelineEvent(mobile, 'manual_note', { note: 'Marked Not Interested', source: 'telesales' }, uid(user));
    },
    markLost: async (mobile, user) => {
        const key = keyOf(mobile);
        await update(ref(db, `customerBase/${key}`), { manualLost: true, updatedAt: Date.now() });
        await recordTimelineEvent(mobile, 'manual_note', { note: 'Marked Lost Customer', source: 'telesales' }, uid(user));
    },

    // --- Do Not Ship / Spam flag ---
    // Writes both the customer record (for the profile) and a small index keyed by
    // last-10-digit mobile (doNotShipIndex/{m10}) so the Orders desk can check a
    // number cheaply without loading the whole customerBase.
    setDoNotShip: async (mobile, on, { reason, note } = {}, user) => {
        const key = keyOf(mobile);
        const m10 = keyOf(mobile);
        if (!key) throw new Error('Invalid mobile');
        const at = Date.now();
        if (on) {
            const payload = clean({ reason: reason || 'Other', note: note || '', by: uid(user), at });
            await update(ref(db, `customerBase/${key}`), { doNotShip: { flagged: true, ...payload }, updatedAt: at });
            await set(ref(db, `doNotShipIndex/${m10}`), payload);
            await recordTimelineEvent(mobile, 'manual_note', {
                note: `🚫 Marked DO NOT SHIP — ${reason || 'Other'}${note ? `: ${note}` : ''}`,
                status: 'do_not_ship', source: 'telesales',
            }, uid(user));
        } else {
            await update(ref(db, `customerBase/${key}`), { doNotShip: null, updatedAt: at });
            await set(ref(db, `doNotShipIndex/${m10}`), null);
            await recordTimelineEvent(mobile, 'manual_note', { note: 'Do Not Ship flag removed', source: 'telesales' }, uid(user));
        }
    },

    // --- Refund Given flag ---
    // Set when a refund is marked "issued" in Support/Complaints. Marks the
    // customer + a small refundGivenIndex/{m10} so the Orders desk can badge the
    // number even on future orders ("refund given — handle with care").
    setRefundGiven: async (mobile, { ticketId, amount, reference } = {}, user) => {
        const key = keyOf(mobile);
        const m10 = keyOf(mobile);
        if (!key) return;
        const at = Date.now();
        const payload = clean({
            ticketId: ticketId || '', amount: amount ? Number(amount) : 0,
            reference: reference || '', by: uid(user), at,
        });
        await update(ref(db, `customerBase/${key}`), { refundGiven: { flagged: true, ...payload }, updatedAt: at });
        await set(ref(db, `refundGivenIndex/${m10}`), payload);
    },
};

export default TelesalesService;
